import React, { Dispatch, SetStateAction, useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Text, useIsMobile } from '@amzn/storm-ui'
import {
  MobileBadgeContainer,
  MobileContactButton,
  MobileContainer,
  PartnerRemoveButton,
  StyledPartnerComparisonCard,
  ComparisonPartnerCardHeader,
  ComparisonPartnerCardBody,
} from './partner_comparison_card.styles'
import servicesIcon from 'assets/images/services_icon_blue.png'
import productsIcon from 'assets/images/products_icon_blue.png'
import certificationsIcon from 'assets/images/certifications_icon_blue.png'
import awardsIcon from 'assets/images/principle_badge_RGB.svg'
import { Accreditation, LeadChannels, Maybe } from 'generated/graphql'
import { directoryStrings } from '../../../directory/directory_i18n_mappings'
import { getKebabPartnerName } from '../../../../util/partner_name_helper'
import { buildBadgeAndHelptip, buildLogo, Distinction } from '../../../../shared/components/shared_components'
import {
  PartnerSummaryFooter,
  CompanyDescription,
  CompanyName,
  PartnerCardContentWrapper,
  PartnerCardDivider,
  StyledAapnLink,
} from '../../../../shared/components/shared_components.styles'
import { ComparedPartnersContext } from '../../../directory/components/partner_comparison/compared_partners_context'
import { TFunction } from 'i18next'
import AdaptiveHelpTip from '@amzn/storm-ui-adaptive-helptip'
import { computedPartnerBadge } from '../../../../util/partner_status_helper'
import { getTooltipMessageKey } from '../../../directory/shared'
import { clickPartnerComparisonCardMetricHandler } from '../../../../util/partner_comparison_metrics_helper'
import { getMetricsPublisherChainAsync } from '@amzn/aapn-front-end-metrics'
import { PartnerPrimaryContactButton } from 'shared/components/contact_action_buttons/contact_action_button'

export interface PartnerComparisonCardProps {
  id: string
  companyName: string
  description: string
  companyLogo: string
  contactUrl: string
  accreditation: Maybe<Accreditation>[]
  numCertifications: number
  numAwards: number
  numProducts: number
  numServices: number
  currentPageSize?: number
  currentPageNumber?: number
  cardIndex?: number
  isLanguageOfPreferenceSupported: boolean
  setIsLanguageNotSupported: Dispatch<SetStateAction<boolean>>
  leadChannels?: LeadChannels | undefined
}

export const PartnerComparisonCard: React.FunctionComponent<PartnerComparisonCardProps> = (props: PartnerComparisonCardProps): JSX.Element => {
  const {
    id,
    companyName,
    description,
    companyLogo,
    accreditation,
    numAwards,
    numProducts,
    numCertifications,
    numServices,
    isLanguageOfPreferenceSupported,
    setIsLanguageNotSupported,
    leadChannels,
  } = props
  const { t } = useTranslation()
  const { removeSelectedPartner } = useContext(ComparedPartnersContext)
  const isMobile = useIsMobile()
  const logo = buildLogo(companyLogo, t)

  useEffect(() => {
    if (!isLanguageOfPreferenceSupported) {
      getMetricsPublisherChainAsync('PartnerComparisonCard').then(p => {
        p.publishString('partnerComparison.page.language.switch.remove.partnerId', id)
      })
      removeSelectedPartner(id)
      setIsLanguageNotSupported(true)
    }
  }, [isLanguageOfPreferenceSupported, removeSelectedPartner, id, setIsLanguageNotSupported])

  const handleClickRemove = (): void => {
    clickPartnerComparisonCardMetricHandler(
      id,
      'partnerComparison.page.partnerComparisonCard.remove.click',
      'partnerComparison.page.partnerComparisonCard.remove.partnerId',
    )
    removeSelectedPartner(id)
  }

  const clickContactPartnerMetrics = (): void => {
    clickPartnerComparisonCardMetricHandler(id, 'partnerComparison.page.contact.click', 'partnerComparison.page.contact.partnerId')
  }

  const buildMobileBadgeAndHelptip = (accreditation: Maybe<Accreditation>[], t: TFunction): JSX.Element => (
    <AdaptiveHelpTip
      aria-haspopup="true"
      renderTrigger={computedPartnerBadge(t, accreditation)}
      tooltipProps={{ position: 'right' }}
      triggerProps={{ style: { textDecoration: 'none', paddingLeft: 0, marginLeft: 0 } }}
    >
      <Text>{accreditation && getTooltipMessageKey(accreditation, t)}</Text>
    </AdaptiveHelpTip>
  )

  const cardNameAndDescription = (
    <>
      <CompanyName type="h2">{companyName}</CompanyName>
      <CompanyDescription textColor="secondary">{description}</CompanyDescription>
    </>
  )

  const desktopContent = (
    <StyledPartnerComparisonCard padding="none">
      <PartnerCardContentWrapper>
        <StyledAapnLink data-testid={`cardLink${id}`} to={`/details/${id}/${getKebabPartnerName(companyName)}`}>
          <ComparisonPartnerCardHeader>
            {logo}
            {buildBadgeAndHelptip(accreditation, t)}
          </ComparisonPartnerCardHeader>
          <ComparisonPartnerCardBody>
            {cardNameAndDescription}
            <Distinction
              iconSrc={servicesIcon}
              iconAlt={t('DIRECTORY:PARTNER_CARD:SERVICES_ICON')}
              distinctioni18nKey={'DIRECTORY:PARTNER_CARD:SERVICES'}
              distinctionCount={numServices}
            />
            <Distinction
              iconSrc={productsIcon}
              iconAlt={t('DIRECTORY:PARTNER_CARD:PRODUCTS_ICON')}
              distinctioni18nKey={'DIRECTORY:PARTNER_CARD:PRODUCTS'}
              distinctionCount={numProducts}
            />
            <Distinction
              iconSrc={certificationsIcon}
              iconAlt={t('DIRECTORY:PARTNER_CARD::CERTIFICATIONS_ICON')}
              distinctioni18nKey={'DIRECTORY:PARTNER_CARD:CERTIFICATIONS'}
              distinctionCount={numCertifications}
            />
            <Distinction
              iconSrc={awardsIcon}
              iconAlt={t('DIRECTORY:PARTNER_CARD:AWARDS_ICON')}
              distinctioni18nKey={'DIRECTORY:PARTNER_CARD:AWARDS'}
              distinctionCount={numAwards}
            />
          </ComparisonPartnerCardBody>
        </StyledAapnLink>
        <PartnerCardDivider />
        <PartnerSummaryFooter>
          <PartnerPrimaryContactButton
            id={id}
            companyName={companyName}
            companyWebsite={props.contactUrl}
            leadChannels={leadChannels}
            beforeAction={clickContactPartnerMetrics}
            concise={true}
          />
          <PartnerRemoveButton id={'remove-partner-button'} onClick={handleClickRemove}>
            {t(directoryStrings.REMOVE)}
          </PartnerRemoveButton>
        </PartnerSummaryFooter>
      </PartnerCardContentWrapper>
    </StyledPartnerComparisonCard>
  )

  const mobileContent = (
    <MobileContainer>
      <MobileBadgeContainer>{buildMobileBadgeAndHelptip(accreditation, t)}</MobileBadgeContainer>
      <StyledAapnLink data-testid={`cardLinkBody${id}`} to={`/details/${id}/${getKebabPartnerName(companyName)}`}>
        <ComparisonPartnerCardBody>
          {cardNameAndDescription}
          <Distinction
            iconSrc={servicesIcon}
            iconAlt={t('DIRECTORY:PARTNER_CARD:SERVICES_ICON')}
            distinctioni18nKey={'DIRECTORY:PARTNER_CARD:SERVICES'}
            distinctionCount={numServices}
          />
          <Distinction
            iconSrc={productsIcon}
            iconAlt={t('DIRECTORY:PARTNER_CARD:PRODUCTS_ICON')}
            distinctioni18nKey={'DIRECTORY:PARTNER_CARD:PRODUCTS'}
            distinctionCount={numProducts}
          />
          <Distinction
            iconSrc={certificationsIcon}
            iconAlt={t('DIRECTORY:PARTNER_CARD::CERTIFICATIONS_ICON')}
            distinctioni18nKey={'DIRECTORY:PARTNER_CARD:CERTIFICATIONS'}
            distinctionCount={numCertifications}
          />
          <Distinction
            iconSrc={awardsIcon}
            iconAlt={t('DIRECTORY:PARTNER_CARD:AWARDS_ICON')}
            distinctioni18nKey={'DIRECTORY:PARTNER_CARD:AWARDS'}
            distinctionCount={numAwards}
          />
        </ComparisonPartnerCardBody>
      </StyledAapnLink>
      <MobileContactButton
        id={id}
        companyName={companyName}
        companyWebsite={props.contactUrl}
        leadChannels={leadChannels}
        beforeAction={clickContactPartnerMetrics}
        concise={true}
      />
      <PartnerRemoveButton onClick={handleClickRemove}>{t(directoryStrings.REMOVE)}</PartnerRemoveButton>
    </MobileContainer>
  )

  return isMobile ? mobileContent : desktopContent
}
